// ============================================================
// _shared_stubs.js
// EV Powertrain Simulator — 공유 초기화 스텁 (Single Source of Truth)
//
// 목적:
//   simulation.js는 zero-import 설계로 인해 battery.js·tire.js를
//   직접 import할 수 없다. 대신 두 파일 모두 이 파일의 구현을 사용하므로
//   시그니처 불일치 리스크를 단일 지점에서 관리한다.
//
// 사용처:
//   - battery.js       → init2RCBatteryState re-export
//   - tire.js          → initRLSMuState re-export
//   - simulation.js    → 아래와 동일한 구현을 인라인 복사 (변경 시 함께 수정)
//
// ★ simulation.js 인라인 스텁을 수정할 경우 반드시 이 파일도 함께 수정할 것.
// ============================================================

/**
 * 2RC 배터리 상태 초기화
 *
 * R0 + (R1‖C1) + (R2‖C2) 등가회로(2RC ECM) 모델의 초기 상태를 생성한다.
 * V1, V2는 각 RC 브랜치의 캐패시터 전압 상태로, 초기에는 0V(과도 성분 없음).
 *
 * simulation.js와 battery.js 양쪽에서 동일한 초기화 로직이 필요하므로
 * 이 파일을 공통 SST(Single Source of Truth)로 관리한다.
 *
 * @param {object} params
 *   params.soc_init - 초기 SOC (기본 0.9 = 90%)
 *   params.T_amb    - 초기 셀 온도 [°C] (기본 25°C)
 * @returns {{ SOC: number, V1: number, V2: number, T_cell: number }}
 *   SOC    : 초기 충전 상태 [0, 1]
 *   V1     : RC1 캐패시터 전압 초기값 [V]
 *   V2     : RC2 캐패시터 전압 초기값 [V]
 *   T_cell : 초기 셀 온도 [°C]
 */
export function init2RCBatteryState(params) {
  return {
    SOC:    params.soc_init ?? 0.9,  // 초기 SOC (기본 90%)
    V1:     0,                        // RC1 과도 전압 초기값 [V]
    V2:     0,                        // RC2 과도 전압 초기값 [V]
    T_cell: params.T_amb   ?? 25,    // 셀 초기 온도 [°C]
  };
}

/**
 * RLS(Recursive Least Squares) 노면 마찰 계수 추정 상태 초기화
 *
 * RLS 알고리즘으로 온라인 노면 마찰(μ)을 추정하기 위한 초기 상태를 생성한다.
 *   mu_est: 추정 마찰 계수 초기값 (파라미터 mu 또는 기본 1.0)
 *   P:      추정 오차 공분산 초기값 (클수록 초기 수렴 빠름)
 *
 * simulation.js와 tire.js 양쪽에서 동일한 초기화가 필요하므로
 * 이 파일을 공통 SST로 관리한다.
 *
 * @param {object} params
 *   params.mu          - 노면 마찰 초기 추정값 (기본 1.0 = 건조 아스팔트)
 *   params.rls_P_init  - 공분산 행렬 초기값 (기본 1.0)
 * @returns {{ mu_est: number, P: number }}
 *   mu_est : 추정 마찰 계수 초기값 [무차원]
 *   P      : 추정 오차 공분산 (스칼라, 단일 상태)
 */
export function initRLSMuState(params) {
  return {
    mu_est: params.mu          ?? 1.0,  // 초기 마찰 계수 추정 (건조 노면 기본값)
    P:      params.rls_P_init  ?? 1.0,  // 공분산 초기값 (높을수록 초기 수렴 빠름)
  };
}
